import { RichText } from '@sitecore-jss/sitecore-jss-nextjs';
import { MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ResultListState } from '@coveo/headless';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { SiteSearchResultRaw } from 'lib/types/coveo/SiteSearchResult';
import { getSiteDetailUrlFromCoveo } from 'components/SiteMaster/utils';
import { SearchLocationFields, LocationSearchCoords } from '../LocationSearch.types';
import LocationSearchSiteCard from './locationSearchSiteCard';
import LocationSearchResultsMap from './locationSearchResultsMap';
import { getClosestSiteIds } from './locationSearchClosest';
import {
  StPaperContainer,
  StResultsList,
  StResultsPanel,
  StResultsSection,
  StShowMoreButton,
  StShowMoreWrap,
  StStatusMessage,
} from './mapCoveoResultToCard.styles';
import { SiteCard } from './mapCoveoResultToCard.types';

const MOBILE_CARDS_PAGE_SIZE = 5;
const VIEW_ALL_SITES_ANCHOR_ID = 'view-all-sites';

type MapCoveoResultToCardProps = {
  results?: ResultListState;
  isLoading: boolean;
  totalCount?: number;
  fields?: SearchLocationFields;
  coords?: LocationSearchCoords | null;
  distanceMiles?: number;
  hasLocationContext?: boolean;
  /** Geocoded "City, ST" the site detail breadcrumb links back with. */
  searchLocality?: string;
  onClearAllFilters?: () => void;
};

export const isRecruitingActiveStatus = (status?: string): boolean => {
  const normalized = (status ?? '').split(';')[0].trim().toLowerCase();
  return /^recruiting(\s+now)?$/.test(normalized);
};

export const hasActiveOrRecruitingStudies = (
  activeStudiesCount?: number,
  recruitingNowCount?: number
): boolean => (activeStudiesCount ?? 0) > 0 || (recruitingNowCount ?? 0) > 0;

export const resolveSiteCardUrl = (
  clickUri?: string,
  siteUrl?: string,
  siteName?: string,
  raw?: Pick<SiteSearchResultRaw, 'site_url' | 'clickableuri' | 'uri' | 'identifiableuri'>
): string => {
  const trimmed = clickUri?.trim();
  if (trimmed) {
    try {
      const { pathname, search, hash } = new URL(trimmed);
      return `${pathname}${search}${hash}`;
    } catch {
      return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
    }
  }

  const resolvedSiteUrl = siteUrl?.trim();
  if (resolvedSiteUrl) {
    return resolvedSiteUrl;
  }

  if (raw) {
    const fromCoveo = getSiteDetailUrlFromCoveo(raw);
    if (fromCoveo) {
      return fromCoveo;
    }
  }

  return `/sites/${encodeURIComponent(siteName ?? '')}`;
};

export const mapCoveoResultToCard = (
  raw: SiteSearchResultRaw,
  fallbackId?: string,
  clickUri?: string
): SiteCard => ({
  id:
    raw.identifiableuri ||
    fallbackId ||
    [raw.site_name, raw.site_address, raw.site_city, raw.site_statecode || raw.site_state]
      .filter(Boolean)
      .join('|'),
  name: raw.site_name ?? '',
  url: resolveSiteCardUrl(clickUri, raw.site_url, raw.site_name, raw),
  city: raw.site_city,
  state: raw.site_statecode || raw.site_state,
  address: raw.site_address,
  distanceMiles: raw.distance,
  activeStudiesCount: raw.site_activestudiescount,
  recruitingNowCount: raw.site_recruitingnowcount,
  conditionsCount: raw.site_conditionscount,
  relatedConditions: raw.site_relatedconditions ?? [],
  latitude: raw.site_lattitude,
  longitude: raw.site_longitude,
  status: raw.site_status,
  isRecruitingActive: hasActiveOrRecruitingStudies(
    raw.site_activestudiescount,
    raw.site_recruitingnowcount
  ),
});

function MapCoveoResultToCard({
  results,
  isLoading,
  fields,
  coords,
  distanceMiles,
  hasLocationContext = false,
  searchLocality,
  onClearAllFilters,
}: MapCoveoResultToCardProps): JSX.Element {
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

  const [visibleCount, setVisibleCount] = useState(MOBILE_CARDS_PAGE_SIZE);
  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null);
  const [selectionTick, setSelectionTick] = useState(0);
  const cardRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const sites = useMemo(
    () =>
      (results?.results ?? []).map((result) =>
        mapCoveoResultToCard(
          result.raw as unknown as SiteSearchResultRaw,
          result.uniqueId,
          result.clickUri
        )
      ),
    [results?.results]
  );

  const resultsSignature = useMemo(() => sites.map((site) => site.id).join('|'), [sites]);

  useEffect(() => {
    setVisibleCount(MOBILE_CARDS_PAGE_SIZE);
    setSelectedSiteId(null);
  }, [resultsSignature]);

  const closestSiteIds = useMemo(() => getClosestSiteIds(sites), [sites]);

  const visibleSites = isDesktop ? sites : sites.slice(0, visibleCount);
  const showMoreButton = !isDesktop && visibleCount < sites.length;

  const closestLabel = fields?.closestLabel?.value || 'CLOSEST';
  const activeLabel = fields?.activeStudiesLabel?.value || 'ACTIVE STUDIES';
  const recruitingLabel = fields?.recruitingNowLabel?.value || 'RECRUITING NOW';
  const conditionsLabel = fields?.conditionsLabel?.value || 'CONDITIONS';
  const viewSiteLabel = fields?.viewSiteLabel?.value || 'View site';
  const notActivelyRecruitingLabel =
    fields?.notActivelyRecruitingLabel?.value || 'Not actively recruiting';
  const showMoreLabel = fields?.showMoreSitesLabel?.value || 'Show 5 more';
  const markerTooltipText = fields?.markerTooltipText?.value;

  const handleSiteSelect = useCallback(
    (siteId: string) => {
      setSelectedSiteId(siteId);
      setSelectionTick((tick) => tick + 1);
      if (!isDesktop) {
        return;
      }
      const cardEl = cardRefs.current[siteId];
      cardEl?.scrollIntoView?.({ behavior: 'smooth', block: 'nearest' });
      cardEl?.focus?.({ preventScroll: true });
    },
    [isDesktop]
  );

  const handleEmptyStateClick = useCallback(
    (event: MouseEvent<HTMLElement>) => {
      const link = (event.target as HTMLElement | null)?.closest?.(`#${VIEW_ALL_SITES_ANCHOR_ID}`);
      if (!link) {
        return;
      }
      event.preventDefault();
      onClearAllFilters?.();
    },
    [onClearAllFilters]
  );

  const cardList = isLoading ? (
    <StResultsList>
      <StStatusMessage>Loading sites…</StStatusMessage>
    </StResultsList>
  ) : sites.length > 0 ? (
    <StResultsList>
      {visibleSites.map((site) => (
        <LocationSearchSiteCard
          key={site.id}
          site={site}
          isClosest={hasLocationContext && closestSiteIds.has(site.id)}
          isSelected={selectedSiteId === site.id}
          closestLabel={closestLabel}
          activeStudiesLabel={activeLabel}
          recruitingNowLabel={recruitingLabel}
          conditionsLabel={conditionsLabel}
          viewSiteLabel={viewSiteLabel}
          notActivelyRecruitingLabel={notActivelyRecruitingLabel}
          cardLocationIcon={fields?.cardLocationIcon}
          siteNameFormat={fields?.siteNameFormat}
          hasLocationContext={hasLocationContext}
          searchLocality={searchLocality}
          onCardMount={(element) => {
            if (element) {
              cardRefs.current[site.id] = element;
            } else {
              delete cardRefs.current[site.id];
            }
          }}
          onSelect={handleSiteSelect}
        />
      ))}
      {showMoreButton && (
        <StShowMoreWrap>
          <StShowMoreButton
            type="button"
            onClick={() =>
              setVisibleCount((count) => Math.min(count + MOBILE_CARDS_PAGE_SIZE, sites.length))
            }
          >
            {showMoreLabel}
          </StShowMoreButton>
        </StShowMoreWrap>
      )}
    </StResultsList>
  ) : (
    <StResultsList>
      <StStatusMessage role="status" onClick={handleEmptyStateClick}>
        {fields?.increaseDistanceFilterText?.value && (
          <RichText field={fields.increaseDistanceFilterText} />
        )}
      </StStatusMessage>
    </StResultsList>
  );

  const mapPanel = (
    <LocationSearchResultsMap
      sites={sites}
      coords={coords}
      distanceMiles={distanceMiles}
      mapLocationIcon={fields?.mapLocationIcon}
      markerTooltipText={markerTooltipText}
      viewSiteLabel={viewSiteLabel}
      siteNameFormat={fields?.siteNameFormat}
      hasLocationContext={hasLocationContext}
      searchLocality={searchLocality}
      selectedSiteId={selectedSiteId}
      selectionTick={selectionTick}
      onSiteSelect={handleSiteSelect}
    />
  );

  return (
    <StResultsSection
      id="location-search-results"
      aria-busy={isLoading || undefined}
      aria-label="Location search results"
    >
      <StResultsPanel>
        {isDesktop ? (
          <StPaperContainer>
            {cardList}
            {mapPanel}
          </StPaperContainer>
        ) : (
          <>
            {mapPanel}
            {cardList}
          </>
        )}
      </StResultsPanel>
    </StResultsSection>
  );
}

export default MapCoveoResultToCard;
